import { useNavigate } from 'react-router-dom';
import React, { useEffect, useMemo, useState } from 'react';
import { getPublishedPage } from '../services/publicCms';
import { ArrowRight, MapPin, Youtube, Instagram, Music2, X } from 'lucide-react';
import { Branch } from '../types/db';
import { PriceSimulator } from '../PriceSimulator';

interface CmsSection {
  id: string;
  type: string;
  position: number;
  is_active: boolean;
  content: any;
}

interface CmsPage {
  id: string;
  slug: string;
  title: string;
  cms_sections: CmsSection[];
}

interface LandingPageProps {
  branches: Branch[];
  onLoginClick: () => void;
  onRegisterClick: () => void;
}

export const LandingPage: React.FC<LandingPageProps> = ({
  branches,
  onLoginClick,
  onRegisterClick,
}) => {
  const [page, setPage] = useState<CmsPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [zoomImage, setZoomImage] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<string>('about');
  const [scrolled, setScrolled] = useState(false);
  const [heroOffset, setHeroOffset] = useState(0);

  useEffect(() => {
    getPublishedPage('home')
      .then((p) => {
        setPage(p);
        if (p?.title) document.title = p.title;
      })
      .finally(() => setLoading(false));
  }, []);

  const sections = useMemo(() => {
    if (!page) return [];
    return [...page.cms_sections]
      .filter((s) => s.is_active)
      .sort((a, b) => a.position - b.position);
  }, [page]);

  useEffect(() => {
    const ids = ['about', 'youtube', 'pricing', 'activities', 'success_stories', 'connect_with_us'];

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { threshold: 0.4 }
    );

    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [sections]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
      setHeroOffset(window.scrollY * 0.2);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (loading)
    return (
      <div className="h-screen flex items-center justify-center text-slate-400 font-bold">
        Loading…
      </div>
    );

  if (!page)
    return (
      <div className="h-screen flex items-center justify-center text-slate-400">
        Website belum dipublish
      </div>
    );

  const branding = sections.find((s) => s.type === 'branding');
  const hero = sections.find((s) => s.type === 'hero');

  return (
    <div className="w-full overflow-x-hidden font-sans text-slate-900 scroll-smooth">
      <Navbar
        logo={branding?.content?.logo}
        brandName={branding?.content?.name || 'DELTA'}
        motto={branding?.content?.motto}
        onLogin={onLoginClick}
        onRegister={onRegisterClick}
        activeSection={activeSection}
        scrolled={scrolled}
      />

      {hero && (
        <Hero
          title={hero.content?.title}
          subtitle={hero.content?.subtitle}
          image={hero.content?.image}
          onRegister={onRegisterClick}
          offset={heroOffset}
        />
      )}

      {sections.map((section) => {
        if (section.type === 'branding' || section.type === 'hero') return null;

        switch (section.type) {
          case 'youtube':
            return <YoutubeSection key={section.id} video_id={section.content?.video_id} />;
          case 'about':
            return <About key={section.id} {...section.content} />;
          case 'features':
            return <Features key={section.id} items={section.content?.items} />;
          case 'pricing':
            return <Pricing key={section.id} items={section.content?.items} />;
          case 'activities':
            return (
              <Gallery
                key={section.id}
                id="activities"
                title="Kegiatan"
                items={section.content?.items}
                onZoom={setZoomImage}
              />
            );
          case 'success_stories':
            return (
              <Gallery
                key={section.id}
                id="success_stories"
                title="Testimoni"
                items={section.content?.items}
                onZoom={setZoomImage}
              />
            );
          case 'connect_with_us':
            return <ConnectWithUs key={section.id} {...section.content} />;
          default:
            return null;
        }
      })}

      <PriceSimulator branches={branches} onRegisterClick={onRegisterClick} />

      <BranchesSection branches={branches} onRegister={onRegisterClick} />
      <Footer />

      {zoomImage && <ImageLightbox src={zoomImage} onClose={() => setZoomImage(null)} />}
    </div>
  );
};

const Navbar = ({ logo, brandName, motto, onLogin, onRegister, activeSection, scrolled }: any) => {
  const [open, setOpen] = useState(false);

  const menuItems = [
    { label: 'Tentang', id: 'about' },
    { label: 'Video', id: 'youtube' },
    { label: 'Program', id: 'pricing' },
    { label: 'Kegiatan', id: 'activities' },
    { label: 'Testimoni', id: 'success_stories' },
    { label: 'Kontak', id: 'connect_with_us' },
  ];

  return (
    <div
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-md backdrop-blur' : 'bg-white/80 backdrop-blur'
      } border-b`}
    >
      <div className="max-w-7xl mx-auto px-6 py-3 flex justify-between items-center">
        {/* Logo */}
        <div className="flex items-center gap-3">
          {logo && <img src={logo} alt={brandName} className="h-9" />}
          <div>
            <div className="font-black">{brandName}</div>
            {motto && <div className="text-xs text-slate-500">{motto}</div>}
          </div>
        </div>

        {/* Desktop Menu */}
        <div className="hidden md:flex items-center gap-8">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() =>
                document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth' })
              }
              className={`text-sm font-semibold transition ${
                activeSection === item.id
                  ? 'text-indigo-600'
                  : 'text-slate-700 hover:text-indigo-600'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>

        {/* Desktop Buttons */}
        <div className="hidden md:flex gap-3">
          <button
            onClick={onLogin}
            className="text-sm font-semibold hover:text-indigo-600 transition"
          >
            Login
          </button>
          <button
            onClick={onRegister}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-full text-sm font-semibold transition"
          >
            Daftar
          </button>
        </div>

        {/* Mobile */}
        <button onClick={() => setOpen(!open)} className="md:hidden text-2xl">
          ☰
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-white border-t px-6 py-6 space-y-4 shadow-lg">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => {
                document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth' });
                setOpen(false);
              }}
              className="block w-full text-left font-semibold"
            >
              {item.label}
            </button>
          ))}

          <hr />

          <button
            onClick={() => {
              onLogin();
              setOpen(false);
            }}
            className="block w-full text-left font-semibold"
          >
            Login
          </button>

          <button
            onClick={() => {
              onRegister();
              setOpen(false);
            }}
            className="block w-full text-left font-semibold text-indigo-600"
          >
            Daftar
          </button>
        </div>
      )}
    </div>
  );
};

const Hero = ({ title, subtitle, image, onRegister, offset }: any) => (
  <section className="relative min-h-[80vh] overflow-hidden">
    {image && (
      <img
        src={image}
        alt={title}
        style={{ transform: `translateY(${offset}px)` }}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-200"
      />
    )}

    <div className="relative min-h-[80vh] bg-gradient-to-r from-black/80 via-black/60 to-black/30 flex items-center">
      <div className="max-w-7xl mx-auto px-6 text-white">
        <h1 className="text-5xl font-black mb-6 leading-tight">{title}</h1>
        <p className="max-w-xl text-white/80 mb-8">{subtitle}</p>
        <button
          onClick={onRegister}
          className="bg-indigo-600 hover:bg-indigo-700 px-8 py-4 rounded-full font-semibold flex items-center gap-2 transition"
        >
          Daftar Sekarang <ArrowRight size={18} />
        </button>
      </div>
    </div>
  </section>
);

const About = ({ title, description }: any) => (
  <section id="about" className="py-28 px-6 max-w-4xl mx-auto text-center">
    <h2 className="text-3xl font-black mb-6">{title}</h2>
    <p className="text-slate-600 leading-relaxed">{description}</p>
  </section>
);

const YoutubeSection = ({ video_id }: { video_id?: string }) =>
  video_id ? (
    <section id="youtube" className="py-28 bg-slate-50">
      <div className="max-w-5xl mx-auto px-6">
        <h2 className="text-3xl font-black text-center mb-12">Profil DELTA</h2>

        <div className="aspect-video rounded-2xl overflow-hidden shadow-2xl border bg-black">
          <iframe
            src={`https://www.youtube.com/embed/${video_id}`}
            title="Video Profil DELTA"
            className="w-full h-full"
            allowFullScreen
          />
        </div>
      </div>
    </section>
  ) : null;

const Features = ({ items }: any) =>
  items?.length ? (
    <section id="features" className="py-28 bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-3 gap-10">
        {items.map((f: any, i: number) => (
          <div
            key={i}
            className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-xl hover:-translate-y-2 transition duration-300"
          >
            <h3 className="font-bold mb-3 text-lg">{f.title}</h3>
            <p className="text-slate-600 text-sm">{f.description}</p>
          </div>
        ))}
      </div>
    </section>
  ) : null;

const Pricing = ({ items }: any) => {
  const navigate = useNavigate();

  if (!items?.length) return null;

  return (
    <section id="pricing" className="py-28">
      <div className="max-w-6xl mx-auto px-6 text-center mb-14">
        <h2 className="text-3xl font-black">Program Belajar</h2>
      </div>

      <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-3 gap-10">
        {items.map((p: any, i: number) => (
          <div
            key={i}
            className="border rounded-2xl p-8 hover:shadow-2xl hover:-translate-y-2 transition duration-300 text-center"
          >
            <h3 className="font-black text-lg mb-3">{p.title}</h3>

            <p className="text-slate-600 text-sm mb-6">{p.description}</p>

            <button
              onClick={() => navigate('/program')}
              className="text-indigo-600 font-semibold hover:underline"
            >
              Klik Selengkapnya →
            </button>
          </div>
        ))}
      </div>
    </section>
  );
};

const Gallery = ({ id, title, items, onZoom }: any) =>
  items?.length ? (
    <section id={id} className="py-28">
      <div className="max-w-7xl mx-auto px-6">
        <h2 className="text-3xl font-black mb-12">{title}</h2>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {items.map((it: any, i: number) => (
            <div
              key={i}
              className="overflow-hidden rounded-2xl group cursor-zoom-in"
              onClick={() => onZoom(it.image)}
            >
              <img
                src={it.image}
                alt={`${title} ${i}`}
                className="w-full aspect-square object-cover transition duration-500 group-hover:scale-110"
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  ) : null;

const ConnectWithUs = ({ youtube, instagram, tiktok, x }: any) => (
  <section id="connect_with_us" className="py-28 bg-slate-900 text-white text-center">
    <h2 className="text-3xl font-black mb-10">Connect With Us</h2>

    <div className="flex justify-center gap-8">
      {youtube && (
        <IconLink href={youtube}>
          <Youtube />
        </IconLink>
      )}
      {instagram && (
        <IconLink href={instagram}>
          <Instagram />
        </IconLink>
      )}
      {tiktok && (
        <IconLink href={tiktok}>
          <Music2 />
        </IconLink>
      )}
      {x && (
        <IconLink href={x}>
          <X />
        </IconLink>
      )}
    </div>
  </section>
);

const IconLink = ({ href, children }: any) => (
  <a
    href={href}
    target="_blank"
    rel="noreferrer"
    className="w-14 h-14 rounded-full bg-white text-black flex items-center justify-center hover:scale-110 hover:shadow-xl transition duration-300"
  >
    {children}
  </a>
);

const BranchesSection = ({
  branches,
  onRegister,
}: {
  branches: Branch[];
  onRegister: () => void;
}) => (
  <section className="py-28 bg-slate-50">
    <div className="max-w-7xl mx-auto px-6">
      <h2 className="text-3xl font-black mb-12">Pilih Cabang</h2>

      <div className="grid md:grid-cols-3 gap-8">
        {branches
          .filter((b) => b.is_active !== false)
          .map((b) => (
            <div
              key={b.id}
              className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-xl hover:-translate-y-2 transition duration-300"
            >
              <MapPin className="text-indigo-600 mb-4" />
              <h3 className="font-black mb-2">{b.name}</h3>
              <p className="text-sm text-slate-500 mb-4">{b.address || 'Alamat belum tersedia'}</p>
              <button
                onClick={onRegister}
                className="text-indigo-600 font-semibold hover:underline"
              >
                Daftar →
              </button>
            </div>
          ))}
        {branches.filter((b) => b.is_active !== false).length === 0 && (
          <div className="col-span-full py-20 text-center border-4 border-dashed rounded-[3rem] border-slate-100 text-slate-300 font-black uppercase text-xs tracking-widest">
            Data Cabang Belum Tersedia
          </div>
        )}
      </div>
    </div>
  </section>
);

const ImageLightbox = ({ src, onClose }: { src: string; onClose: () => void }) => (
  <div
    onClick={onClose}
    className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-6 backdrop-blur-sm"
  >
    <img
      src={src}
      alt="Preview gambar"
      className="max-w-full max-h-full rounded-2xl shadow-2xl animate-fade"
      onClick={(e) => e.stopPropagation()}
    />
  </div>
);

const Footer = () => (
  <footer className="py-14 bg-slate-950 text-slate-400 text-center text-sm">
    © {new Date().getFullYear()} DELTA • All Rights Reserved
  </footer>
);
